import { api } from './client';

export interface DeviceReport {
  id: string;
  device_id: string;
  bundle_id: string;
  has_sim: number;
  sim_country: string;
  ip_country: string;
  geo_match: number;
  detected_packages: string;
  device_clean: number;
  sdk_status: string;
  block_reason: string;
  device_model: string;
  os_version: string;
  battery_level: number | null;
  is_charging: number | null;
  created_at: string;
  updated_at: string;
  // Ad delivery funnel — written by /ads/request, /events/impression and
  // /events/webview as the SDK progresses. last_ad_outcome reflects the most
  // recent state for this device+bundle pair.
  last_ad_outcome: string | null;
  last_ad_outcome_at: string | null;
  last_ad_id: string | null;
  last_ad_type: string | null;
  webview_shown_at: string | null;
  webview_fail_reason: string | null;
  // Сигнал акселерометра, пишется вторым вызовом /device/report.
  // 'none' означает «SDK этой версии ещё не доехал», а НЕ «чисто».
  // Единицы: motion_stddev и motion_mean_g — мм/с², motion_dup_pct — проценты.
  motion_flag: string;
  motion_stddev: number | null;
  motion_dup_pct: number | null;
  motion_mean_g: number | null;
  motion_posture: string | null;
  motion_events: number | null;
  motion_samples: number | null;
  motion_has_gyro: number | null;
  motion_at: string | null;
  motion_raw: string | null;
  // Счётчики скриншотов, снятых внутри приложения (см. вкладку Screenshots).
  // Пишутся из /device/screenshot, на блокировки не влияют.
  screenshot_count: number | null;
  last_screenshot_at: string | null;
  // Последняя проверка «человек или бот» по device_id (Human check).
  // null — проверки не было: показываем «—», а не 0 («точно человек»).
  session_bot_probability: number | null;
  session_confidence: number | null;
}

export interface DeviceStats {
  total: number;
  active: number;
  blocked: number;
  by_reason: {
    no_sim: number;
    geo_mismatch: number;
    suspicious_apps: number;
    review_visit?: number;
    motion_frozen?: number;
    tz_mismatch?: number;
    battery_full?: number;
    device_burst?: number;
    bot_fleet?: number;
  };
  by_outcome?: Record<string, number>;
  by_motion?: Record<string, number>;
}

export interface DeviceListResponse {
  devices: DeviceReport[];
  total: number;
  page: number;
  limit: number;
}

export type SortDir = 'asc' | 'desc';

export interface BundleInfo {
  bundle_id: string;
  count: number;
}

export interface FacetValue {
  value: string;
  count: number;
}

export interface DeviceFacets {
  device_models: FacetValue[];
  os_versions: FacetValue[];
}

export interface MotionBucket {
  bucket: string;
  count: number;
}

export interface DeviceQuery {
  status?: string[];
  reason?: string[];
  outcome?: string[];
  motion?: string[];
  country?: string[];
  simCountry?: string[];
  bundle?: string[];
  deviceModel?: string[];
  osVersion?: string[];
  hasSim?: 'yes' | 'no' | 'all';
  geoMatch?: 'match' | 'mismatch' | 'all';
  deviceClean?: 'clean' | 'dirty' | 'all';
  search?: string;
  dateFrom?: string;
  dateTo?: string;
  dateField?: 'updated_at' | 'created_at';
  sortBy?: string;
  sortDir?: SortDir;
  page?: number;
  limit?: number;
}

// Builds the filter-only query string shared by the list and the stats
// summary. Sort/pagination are appended separately by getDevices.
function buildFilterParams(params: DeviceQuery): URLSearchParams {
  const query = new URLSearchParams();
  const setMulti = (key: string, vals?: string[]) => {
    if (vals && vals.length > 0) query.set(key, vals.join(','));
  };
  setMulti('status', params.status);
  setMulti('reason', params.reason);
  setMulti('outcome', params.outcome);
  setMulti('motion', params.motion);
  setMulti('country', params.country);
  setMulti('sim_country', params.simCountry);
  setMulti('bundle', params.bundle);
  setMulti('device_model', params.deviceModel);
  setMulti('os_version', params.osVersion);
  if (params.hasSim && params.hasSim !== 'all') query.set('has_sim', params.hasSim);
  if (params.geoMatch && params.geoMatch !== 'all') query.set('geo_match', params.geoMatch);
  if (params.deviceClean && params.deviceClean !== 'all') query.set('device_clean', params.deviceClean);
  if (params.search) query.set('search', params.search);
  if (params.dateFrom) query.set('date_from', params.dateFrom);
  if (params.dateTo) query.set('date_to', params.dateTo);
  if (params.dateField) query.set('date_field', params.dateField);
  return query;
}

export async function getDeviceStats(params: DeviceQuery = {}): Promise<DeviceStats> {
  try {
    const qs = buildFilterParams(params).toString();
    return await api.get<DeviceStats>(`/admin/devices/stats${qs ? `?${qs}` : ''}`);
  } catch {
    return { total: 0, active: 0, blocked: 0, by_reason: { no_sim: 0, geo_mismatch: 0, suspicious_apps: 0 } };
  }
}

export async function getDeviceBundles(): Promise<BundleInfo[]> {
  try {
    const res = await api.get<{ bundles: BundleInfo[] }>('/admin/devices/bundles');
    return res.bundles || [];
  } catch {
    return [];
  }
}

export async function getDeviceFacets(): Promise<DeviceFacets> {
  try {
    return await api.get<DeviceFacets>('/admin/devices/facets');
  } catch {
    return { device_models: [], os_versions: [] };
  }
}

export async function getMotionHistogram(params: DeviceQuery = {}): Promise<MotionBucket[]> {
  try {
    const qs = buildFilterParams(params).toString();
    const res = await api.get<{ buckets: MotionBucket[] }>(
      `/admin/devices/motion-histogram${qs ? `?${qs}` : ''}`
    );
    return res.buckets || [];
  } catch {
    return [];
  }
}

export async function getDevices(params: DeviceQuery): Promise<DeviceListResponse> {
  try {
    const query = buildFilterParams(params);
    if (params.sortBy) query.set('sort_by', params.sortBy);
    if (params.sortDir) query.set('sort_dir', params.sortDir);
    if (params.page) query.set('page', params.page.toString());
    if (params.limit) query.set('limit', params.limit.toString());
    return await api.get<DeviceListResponse>(`/admin/devices?${query.toString()}`);
  } catch {
    return { devices: [], total: 0, page: 1, limit: 50 };
  }
}
